/**
 * useAnnouncements Hook
 * 
 * React hooks for fetching and managing announcements.
 */

import { useState, useEffect, useCallback } from 'react';
import { Announcement } from '@/types/announcements';
import { announcementService } from '@/lib/announcement-service';

interface UseAnnouncementsReturn {
  announcements: Announcement[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export function useAnnouncements(): UseAnnouncementsReturn {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const all = await announcementService.getAllAnnouncements();
      setAnnouncements(all);
    } catch (err) {
      console.error('[useAnnouncements] Error:', err);
      setError('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  return {
    announcements,
    loading,
    error,
    refresh: fetchAnnouncements,
  };
}

/**
 * Hook specifically for getting the active announcement (for public display)
 */
export function useActiveAnnouncement() {
  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function fetchActive() {
      try {
        const active = await announcementService.getActiveAnnouncement();
        if (mounted) {
          setAnnouncement(active);
        }
      } catch (error) {
        console.error('[useActiveAnnouncement] Error:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    }

    fetchActive();

    return () => {
      mounted = false;
    };
  }, []);

  const trackImpression = useCallback(async () => {
    if (announcement?.id) {
      await announcementService.trackImpression(announcement.id);
    }
  }, [announcement?.id]);

  const trackClick = useCallback(async () => {
    if (announcement?.id) {
      await announcementService.trackClick(announcement.id);
    }
  }, [announcement?.id]);

  return {
    announcement,
    loading,
    trackImpression,
    trackClick,
  };
}

